// apps/merchant-v3/src/features/support/supportRealtime.tsx
//
// dbg-20260705-ticket-realtime-notify — merchant support realtime:
//   • useSupportUnreadCount — unread rows from the shared ticket list cache
//   • MerchantSupportBadge  — sidebar trailing pill
//   • MerchantSupportRealtime — global listener: ws ticket.reply/created →
//     refetch + sound + desktop notification + tab flash (admin replies)
//
// Shares the `['support-tickets']` query cache with the Support page, so the
// badge and page stay in sync off a single fetch.

import { useCallback, useEffect, type JSX } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ensureNotificationPermission,
  showDesktopNotification,
  flashTitle,
} from '@printway/ui';

import type { WsServerEvent } from '@printway/ui';

import { getTicketUnreadCount } from '@/services/supportClient';
import { useWsEvent } from '@/lib/websocket/WsProvider';

const SUPPORT_TICKETS_KEY = ['support-tickets'] as const;
const SUPPORT_UNREAD_KEY = ['support-unread-count'] as const;

export function useSupportUnreadCount(): number {
  const q = useQuery({
    queryKey: SUPPORT_UNREAD_KEY,
    // Server-side count. This used to fetch 100 ticket rows and filter
    // `unread === true` in the browser — ~14KB every 15 seconds, for the life
    // of the session, to render one number. GET /tickets/unread-count answers
    // with the integer, scoped by the same resolver the list uses.
    queryFn: getTicketUnreadCount,
    staleTime: 15_000,
    // Background poll (dbg-20260708): reading a ticket clears its unread flag
    // server-side but fires no WS event (WS only covers inbound replies), so
    // the sidebar badge would otherwise only refresh on the next navigation.
    refetchInterval: 15_000,
  });
  return q.data?.unread ?? 0;
}

export function MerchantSupportBadge(): JSX.Element | null {
  const count = useSupportUnreadCount();
  if (count <= 0) return null;
  return (
    <span
      data-testid="sidebar-link-support-badge"
      className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[11px] font-semibold leading-none text-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function MerchantSupportRealtime(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('support');

  useEffect(() => {
    void ensureNotificationPermission();
  }, []);

  const onTicketEvent = useCallback(
    (ev: WsServerEvent) => {
      void qc.invalidateQueries({ queryKey: SUPPORT_TICKETS_KEY });
      // The badge no longer shares the ticket-list cache, so it needs its own
      // invalidation or an inbound reply would not light it up until the poll.
      void qc.invalidateQueries({ queryKey: SUPPORT_UNREAD_KEY });
      // Detail pages key off ['support-ticket', id] (singular) — the list-key
      // invalidation above does NOT prefix-match them, which left an open
      // conversation frozen while staff replied (dbg-20260706-162140).
      void qc.invalidateQueries({ queryKey: ['support-ticket'] });
      // Attention (sound/notification/flash) only for the OTHER side's
      // messages — a merchant-authored reply echoes back on the merchant
      // channel and must not chime the sender (dbg-20260706-173837).
      const authorRole = (ev.payload as { authorRole?: string } | undefined)
        ?.authorRole;
      if (authorRole === 'merchant') return;
      // No sound on the merchant side (dbg-20260710: sound is admin-only) —
      // visual attention (tab flash + desktop notification) only.
      flashTitle(t('realtime.tabFlash'));
      showDesktopNotification(t('realtime.updateTitle'), {
        body: t('realtime.updateBody'),
        tag: 'merchant-ticket',
        onClick: () => navigate('/tickets'),
      });
    },
    [qc, navigate, t],
  );

  useWsEvent('ticket.reply', onTicketEvent);
  useWsEvent('ticket.created', onTicketEvent);

  return (
    <span
      aria-hidden="true"
      data-testid="merchant-support-listener"
      className="sr-only"
    />
  );
}
